import { Injectable } from '@angular/core';
import { Observable, catchError, map } from 'rxjs';
import { HttpService } from '../http.service';
import { HttpErrorHandlerService } from '../http-error-handler.service';

/**
 * Estado de una gratificación voluntaria.
 */
export interface GratificationData {
  id: number;
  conversation_id: number;
  sender_id: number;
  receiver_id: number;
  amount: number;
  status: GratificationStatusType;
  paid_at: string | null;
  accepted_at: string | null;
  rejected_at: string | null;
  transferred_at: string | null;
  refunded_at: string | null;
  connect_deadline_at: string | null;
  created_at: string | null;
}

export type GratificationStatusType =
  | 'pending_payment'
  | 'pending_acceptance'
  | 'accepted'
  | 'rejected'
  | 'transferred'
  | 'refunded'
  | 'expired';

export interface GratificationStatusResponse {
  success: boolean;
  gratification: GratificationData | null;
  can_send: boolean;
}

export interface GratificationSendResponse {
  success: boolean;
  gratification_id: number;
  clientSecret: string;
  ephemeralKey: string;
  customerId: string;
}

export interface GratificationSendWebResponse {
  success: boolean;
  gratification_id: number;
  session: { client_secret: string; id: string };
}

export interface GratificationActionResponse {
  success: boolean;
  gratification: GratificationData;
  message?: string;
}

/**
 * Servicio API para gratificaciones voluntarias.
 * Endpoints disponibles:
 *   POST /gratification/{conversationId}/send
 *   POST /gratification/{gratificationId}/accept
 *   POST /gratification/{gratificationId}/reject
 *   POST /gratification/{gratificationId}/retry-transfer
 *   GET  /gratification/{conversationId}/status
 */
@Injectable({ providedIn: 'root' })
export class GratificationService {
  constructor(
    private http: HttpService,
    private errorHandler: HttpErrorHandlerService,
  ) {}

  /**
   * Crea una gratificación y obtiene datos para iniciar el pago con Stripe.
   */
  send(conversationId: number, amount: number): Observable<GratificationSendResponse> {
    return this.http.post(`gratification/${conversationId}/send`, { amount }).pipe(
      map((res: any) => res),
      catchError(err => this.errorHandler.handleError(err)),
    );
  }

  /**
   * Crea una gratificación con Checkout Session para Embedded Checkout (web).
   */
  sendWeb(conversationId: number, amount: number): Observable<GratificationSendWebResponse> {
    return this.http.post(`gratification/${conversationId}/send-web`, { amount }).pipe(
      map((res: any) => res),
      catchError(err => this.errorHandler.handleError(err)),
    );
  }

  /**
   * El Finder acepta la gratificación.
   */
  accept(gratificationId: number): Observable<GratificationActionResponse> {
    return this.http.post(`gratification/${gratificationId}/accept`, {}).pipe(
      map((res: any) => res),
      catchError(err => this.errorHandler.handleError(err)),
    );
  }

  /**
   * El Finder rechaza la gratificación.
   */
  reject(gratificationId: number): Observable<GratificationActionResponse> {
    return this.http.post(`gratification/${gratificationId}/reject`, {}).pipe(
      map((res: any) => res),
      catchError(err => this.errorHandler.handleError(err)),
    );
  }

  /**
   * Reintenta la transferencia tras configurar Stripe Connect.
   */
  retryTransfer(gratificationId: number): Observable<GratificationActionResponse> {
    return this.http.post(`gratification/${gratificationId}/retry-transfer`, {}).pipe(
      map((res: any) => res),
      catchError(err => this.errorHandler.handleError(err)),
    );
  }

  /**
   * Consulta el estado de la gratificación de una conversación.
   */
  getStatus(conversationId: number): Observable<GratificationStatusResponse> {
    return this.http.get<GratificationStatusResponse>(`gratification/${conversationId}/status`).pipe(
      catchError(err => this.errorHandler.handleError(err)),
    );
  }
}
